<template>
  <div class="w-full flex justify-center gap-2">
    <UseExample :src="exampleSrc" />
    <div class="glass-frosted relative rounded-8xl w-250 h-67.5 p-4">
      <div class="p-2">
        <Keyboard
          key-class="flex items-center justify-center gap-1 bg-key text-black-white font-[Barlow] text-xs-semibold! whitespace-nowrap transition-colors cursor-pointer hover:text-text-glass-default active:bg-white active:text-brand-blue2 disabled:text-glass-disabled disabled:cursor-not-allowed disabled:pointer-events-none"
          :keyboard-config="keyboardConfig!"
          :native-event-names="sourceKeyboardEventNames"
          :gap="6"
          @key-event="handleSourceKeyEvent"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, inject } from 'vue'
  import { Keyboard, toast } from '@rapoo/element'
  import { setDragData, DragType } from '@/utils/dnd'
  import { useLocaleStore } from '@/stores/locale'
  import UseExample from '@/components/UseExample.vue'
  import { useDragImage } from '../composables/useDragImage'
  import { fullSize108 } from '@/utils/fullSize108'
  import { KEYCODE_KEYWORDS_MAP, getLocalizedField } from '../utils/keyMapSettings'
  import { SelectedKeySymbol, UpdateSelectedKeyMappingSymbol } from '../types'
  import useExampleZhCN from '@/assets/images/recording/keyboard/normal-zhCN.gif'
  import useExampleEnUS from '@/assets/images/recording/keyboard/normal-enUS.gif'

  import type {
    KeyEventName,
    KeyboardKeyEventPayload,
    KeyboardConfig,
    KeyConfig,
  } from '@rapoo/element'
  import type { KeyChangePayload, UpdateSelectedKeyMapping } from '../types'

  const selectedKey = inject(SelectedKeySymbol)
  const updateSelectedKeyMapping = inject<UpdateSelectedKeyMapping>(UpdateSelectedKeyMappingSymbol)

  const { language } = useLocaleStore()

  const exampleSrc = computed(() => (language === 'zh-CN' ? useExampleZhCN : useExampleEnUS))

  const keyboardConfig = computed<KeyboardConfig | null>(() => {
    const isCybercafe = (import.meta.env.VITE_CHANNEL as string) === 'china_cybercafe'

    return {
      ...fullSize108,
      keys: fullSize108.keys.map((row) =>
        row
          // 网吧版常规键盘去掉 Fn 源键
          .filter((key) => !(isCybercafe && key.keywords === 'Fn'))
          .map((key) => {
            const { w, h, x, y, keywords, ...rest } = key
            const config: KeyConfig = KEYCODE_KEYWORDS_MAP.get(keywords)!

            return {
              ...rest,
              ...config,
              rect: {
                w,
                h,
                x,
                y,
              },
            }
          }),
      ),
    }
  })

  const sourceKeyboardEventNames: KeyEventName[] = ['click', 'contextmenu', 'dragstart', 'dragend']
  const { setDragImage } = useDragImage()

  const handleSourceKeyEvent = (ctx: KeyboardKeyEventPayload) => {
    const { data, eventName, nativeEvent } = ctx

    if (eventName === 'click') {
      if (selectedKey && selectedKey.value && updateSelectedKeyMapping) {
        const payload: KeyChangePayload = {
          keywords: selectedKey.value.keywords,
          keyType: 'normal',
          raw: data,
          target: data,
        }

        updateSelectedKeyMapping(payload)
        selectedKey.value = null
        toast.dismiss('keyboard')
      }
    }

    if (eventName === 'dragstart') {
      const event = nativeEvent as DragEvent
      const dataTransfer = event.dataTransfer
      if (!dataTransfer) return

      const dragData: KeyChangePayload = {
        keywords: '',
        keyType: 'normal',
        raw: data,
        target: data,
      }

      setDragData(dataTransfer, DragType.KEY_MAPPING, dragData)
      // 拖拽过程中显示的按键文本
      setDragImage(event, {
        text: getLocalizedField(data.alias),
      })
    }
  }
</script>

<style scoped></style>
